/**
 * Partnr Canvas Action Executor — Phase 3
 * Receives parsed canvas action payloads from the Partnr stream and executes them locally.
 *
 * S4: every create lands in one insert (canvasStore.createItems) at the
 * positions of the ghosts the ghost layer reserved for that tool call
 * (lib/partnr/ghosts.ts), and every change is recorded in the undo history,
 * so the hook's per-message group undoes a whole turn.
 */

import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { fallbackLinkContent, fetchFreshLinkPreview, linkCardHeight, mergeLinkMetadata, type LinkItemContent } from '@/lib/linkPreview';
import { markdownToHtml } from '@/lib/markdownUtils';
import { useCanvasStore, type Board, type Item, type ItemInput, type ItemType } from '@/stores/canvasStore';
import { useHistoryStore, type HistoryAction, type HistoryGroup } from '@/stores/historyStore';
import { useArrowStore, type Arrow } from '@/stores/arrowStore';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { usePartnrOpenDocumentStore } from '@/lib/partnr/openDocument';
import { alreadyHereMessage, hostOf, newSourcesOnly, sourceKeysOf, titleFromPath } from '@/lib/partnr/sourceDisplay';
import {
  clipCardHeight,
  CLIP_CARD_WIDTH,
  ghostObstacles,
  imageCardSize,
  LINK_CARD_WIDTH,
  flowPositions,
  usePartnrGhostStore,
  type GhostBlock,
  type GhostSpec,
} from '@/lib/partnr/ghosts';
import { usePartnrConfirmStore } from '@/stores/partnrConfirmStore';
import { calculatePlacement, ensureNoCollision } from './canvasPlacement';
import { stickyColorFields, toStickyColorName } from './stickyColors';
import {
  computeGridLayout,
  computeHorizontalLayout,
  computeVerticalLayout,
  computeRadialLayout,
  computeClusterLayout,
} from './layoutAlgorithms';
import { applyLayout, getDefaultWidth, getDefaultHeight } from './canvasActions';
import { computeDagreLayout, getDagreDirection, getDagreConnectors } from './dagreLayout';
import { runExtensionFromPartnr } from '@/extensions/runFromPartnr';

export interface CanvasAction {
  action: string;
  payload: Record<string, unknown>;
  confirmation?: string | null;
}

/** Where an action came from. Partnr 2 passes the tool call (its ghosts) and the reply's undo group. */
export interface CanvasActionContext {
  toolCallId?: string;
  /** Every history step of this action joins this group. Default: a group of its own, so one action is one undo step. */
  group?: HistoryGroup;
}

/** What each handler needs to know about the action it runs. */
interface ExecContext {
  ghostKey?: string;
  group: HistoryGroup;
}

let ungrouped = 0;

/** Puts a step in the undo history, inside the action's group. */
function record(ctx: ExecContext, action: HistoryAction): void {
  useHistoryStore.getState().pushAction(action, ctx.group);
}

/**
 * Execute a canvas action received from the Partnr stream.
 */
export async function executeCanvasAction(action: CanvasAction, context: CanvasActionContext = {}): Promise<void> {
  console.log('[CanvasAction] Executing:', action.action, action.payload);
  const ctx: ExecContext = {
    ghostKey: context.toolCallId,
    group: context.group ?? { key: `action-${Date.now().toString(36)}-${++ungrouped}`, label: "Partnr's changes" },
  };

  switch (action.action) {
    case 'create_canvas_items':
      await handleCreateItems(action.payload, ctx);
      break;
    case 'arrange_layout':
      await handleArrangeLayout(action.payload);
      break;
    case 'create_connections':
      await handleCreateConnections(action.payload, ctx);
      break;
    case 'create_diagram':
      await handleCreateDiagram(action.payload, ctx);
      break;
    case 'focus_viewport':
      await handleFocusViewport(action.payload);
      break;
    case 'create_folder':
      await handleCreateFolder(action.payload, ctx);
      break;
    case 'add_links':
      await handleAddLinks(action.payload, ctx);
      break;
    case 'add_images':
      await handleAddImages(action.payload, ctx);
      break;
    case 'clip_to_board':
      await handleClipToBoard(action.payload, ctx);
      break;
    case 'create_document':
      await handleCreateDocument(action.payload, ctx);
      break;
    case 'update_items':
      await handleUpdateItems(action.payload, ctx);
      break;
    case 'move_items':
      await handleMoveItems(action.payload, ctx);
      break;
    case 'delete_items':
      await handleDeleteItems(action.payload, ctx);
      break;
    case 'run_extension':
      await runExtensionFromPartnr(action.payload);
      break;
  }
}

/**
 * Pull every complete canvas action out of streamed text, in order.
 * An action still arriving (no closing `-->` yet) is left in remainingText.
 */
export function extractCanvasActions(
  text: string
): { actions: CanvasAction[]; remainingText: string } {
  const actions: CanvasAction[] = [];
  const remainingText = text
    .replace(/<!--CANVAS_ACTION:([\s\S]*?)-->/g, (_match, json: string) => {
      try {
        actions.push(JSON.parse(json) as CanvasAction);
      } catch (e) {
        console.error('[CanvasAction] Failed to parse:', e);
      }
      return '';
    })
    .trim();
  return { actions, remainingText };
}

// ---------------------------------------------------------------------------
// Action Handlers
// ---------------------------------------------------------------------------

async function handleCreateItems(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const store = useCanvasStore.getState();
  const items = payload.items as Array<{
    item_type: string;
    title: string;
    body: string;
    color?: string;
  }>;

  if (!Array.isArray(items) || items.length === 0) {
    console.error('[CanvasAction] handleCreateItems: items is empty or invalid', payload);
    return;
  }

  const newItems = items.map(item => ({
    item_type: item.item_type as ItemType,
    content: buildContentJsonb(item),
    width: getDefaultWidth(item.item_type),
    height: getDefaultHeight(item.item_type),
  }));

  // Created inside a folder: no ghosts, the user isn't looking at that board.
  const targetBoardId = payload.board_id as string | undefined;
  if (targetBoardId && targetBoardId !== store.currentBoardId) {
    await placeItemsOnBoard(ctx, targetBoardId, newItems);
    return;
  }

  const layout = (payload.layout as string) || 'grid';
  const created = await landOnCurrentBoard({
    ctx,
    toolName: 'create_canvas_items',
    items: newItems,
    placement: payload.placement as CreatePlacement | undefined,
    layoutPositions: (sized) => computeLayoutPositions(sized, layout, 0, 0),
    ghostSpecs: items.map(item => ({
      kind: 'card',
      itemType: item.item_type,
      title: item.title ?? '',
      body: item.body ?? '',
      color: item.color,
      width: getDefaultWidth(item.item_type),
      height: getDefaultHeight(item.item_type),
      complete: true,
    })),
  });

  if (created.length > 0) {
    if (!ctx.ghostKey) await bringIntoView(created.map(i => i.id));
  }
}

async function handleArrangeLayout(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const itemIds = (payload.resolvedItemIds as string[] | null) || store.items.map(i => i.id);
  const layoutType = (payload.layout_type as string) || 'grid';

  const items = itemIds
    .map(id => store.items.find(i => i.id === id))
    .filter(Boolean)
    .map(item => ({
      id: item!.id,
      width: item!.width ?? 200,
      height: item!.height ?? 150,
    }));

  if (items.length === 0) return;

  const viewportCenter = visibleViewportCenter();

  // Smart layout: if items have existing connections, use dagre for topology-aware layout
  const itemIdSet = new Set(items.map(i => i.id));
  const arrowState = useArrowStore.getState();
  const relevantEdges = arrowState.arrows
    .filter(a => a.start_item_id && a.end_item_id && itemIdSet.has(a.start_item_id) && itemIdSet.has(a.end_item_id))
    .map(a => ({ source: a.start_item_id!, target: a.end_item_id! }));

  let positions: Map<string, { x: number; y: number }>;

  if (relevantEdges.length > 0 && layoutType !== 'cluster' && layoutType !== 'radial') {
    // Use dagre for connected items
    const direction = layoutType === 'horizontal_row' ? 'LR' as const : 'TB' as const;
    positions = computeDagreLayout(items, relevantEdges, {
      direction,
      centerX: viewportCenter.x,
      centerY: viewportCenter.y,
    });
  } else {
    positions = computeLayoutPositions(items, layoutType, viewportCenter.x, viewportCenter.y);
  }

  await applyLayout(positions, { animate: true });
}

async function handleCreateConnections(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const connections = payload.connections as Array<{
    sourceItemId: string;
    targetItemId: string;
    label?: string;
    connection_type?: string;
    style?: string;
  }>;

  const boardId = useCanvasStore.getState().currentBoardId;
  if (!boardId || !Array.isArray(connections)) return;

  const arrows: Array<Partial<Arrow>> = connections
    .filter(conn => conn.sourceItemId && conn.targetItemId)
    .map(conn => ({
      board_id: boardId,
      start_item_id: conn.sourceItemId,
      end_item_id: conn.targetItemId,
      start_connector: 'right',
      end_connector: 'left',
      label: conn.label,
      connection_type: (conn.connection_type as 'default' | 'causes' | 'blocks' | 'contains' | 'sequence' | 'related') || 'default',
      style: (conn.style as 'bezier' | 'straight' | 'step') || 'bezier',
      stroke_color: getConnectionTypeColor(conn.connection_type),
      stroke_width: 2,
      animated: false,
      start_arrowhead: false,
      end_arrowhead: true,
      created_by: 'ai',
    }));
  if (arrows.length === 0) return;
  const created = await useArrowStore.getState().createArrowsBatch(arrows);
  recordArrows(ctx, created, arrows);
}

/** Puts Partnr's new arrows in the undo history (redo recreates them from the data). */
function recordArrows(ctx: ExecContext, created: Arrow[], data: Array<Partial<Arrow>>): void {
  if (created.length === 0) return;
  record(ctx, {
    type: 'CREATE_ARROWS',
    timestamp: Date.now(),
    arrowIds: created.map(a => a.id),
    // Redo recreates them settled: the animation only plays once, at creation
    newContent: { arrows: data.map(a => ({ ...a, animated: false })) },
  });
}

interface DiagramNode {
  id: string;
  item_type: string;
  title: string;
  body?: string;
  color?: string;
  role?: string;
}

interface DiagramEdge {
  source_id: string;
  target_id: string;
  label?: string;
  style?: string;
}

/**
 * A diagram's layout around (0, 0): dagre when it has edges, else a simple
 * layout for its type. The ghost layer uses it too, so a finished diagram's
 * ghosts move to exactly where the nodes will land.
 */
export function diagramPositions(nodes: DiagramNode[], edges: DiagramEdge[], diagramType: string): Map<string, { x: number; y: number }> {
  const sized = nodes.map(node => ({
    id: node.id,
    width: getDefaultWidth(node.item_type),
    height: getDefaultHeight(node.item_type),
  }));
  if (edges.length > 0 && diagramType !== 'comparison') {
    return computeDagreLayout(
      sized,
      edges.map(e => ({ source: e.source_id, target: e.target_id })),
      { direction: getDagreDirection(diagramType), centerX: 0, centerY: 0 },
    );
  }
  // Fallback for diagrams without edges or comparison type
  const layoutMap: Record<string, string> = {
    flowchart: 'vertical_column',
    mind_map: 'radial',
    process: 'horizontal_row',
    hierarchy: 'vertical_column',
    comparison: 'grid',
  };
  return computeLayoutPositions(sized, layoutMap[diagramType] || 'grid', 0, 0);
}

async function handleCreateDiagram(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  if (!Array.isArray(payload.nodes) || (payload.nodes as unknown[]).length === 0) {
    console.error('[CanvasAction] handleCreateDiagram: nodes is empty or invalid', payload);
    return;
  }

  const nodes = payload.nodes as DiagramNode[];
  const edges = (Array.isArray(payload.edges) ? payload.edges : []) as DiagramEdge[];
  const diagramType = (payload.diagram_type as string) || 'flowchart';
  const dagreDirection = getDagreDirection(diagramType);
  const laidOut = diagramPositions(nodes, edges, diagramType);

  const created = await landOnCurrentBoard({
    ctx,
    toolName: 'create_diagram',
    items: nodes.map(node => ({
      item_type: node.item_type as ItemType,
      content: buildContentJsonb({ ...node, color: node.color || getRoleColor(node.role) }),
      width: getDefaultWidth(node.item_type),
      height: getDefaultHeight(node.item_type),
    })),
    layoutPositions: () => new Map(nodes.map((node, i) => [String(i), laidOut.get(node.id) ?? { x: 0, y: 0 }])),
    ghostSpecs: nodes.map(node => ({
      kind: 'node',
      itemType: node.item_type,
      title: node.title ?? '',
      body: node.body ?? '',
      color: node.color || getRoleColor(node.role),
      width: getDefaultWidth(node.item_type),
      height: getDefaultHeight(node.item_type),
      complete: true,
      nodeId: node.id,
    })),
  });

  // node temp id -> real id; createItems keeps input order
  const idMap = new Map<string, string>();
  created.forEach((item, i) => idMap.set(nodes[i].id, item.id));

  // Connections in one insert, after the nodes exist
  const boardId = useCanvasStore.getState().currentBoardId;
  if (!boardId) return;
  const connectors = getDagreConnectors(dagreDirection);
  const arrows: Array<Partial<Arrow>> = edges
    .filter(edge => idMap.has(edge.source_id) && idMap.has(edge.target_id))
    .map(edge => ({
      board_id: boardId,
      start_item_id: idMap.get(edge.source_id)!,
      end_item_id: idMap.get(edge.target_id)!,
      start_connector: connectors.startConnector,
      end_connector: connectors.endConnector,
      label: edge.label,
      style: (edge.style as 'bezier' | 'straight' | 'step') || getDefaultEdgeStyle(diagramType),
      connection_type: 'sequence',
      stroke_color: '#94a3b8',
      stroke_width: 2,
      animated: true,
      start_arrowhead: false,
      end_arrowhead: true,
      created_by: 'ai',
    }));
  const createdArrows = arrows.length ? await useArrowStore.getState().createArrowsBatch(arrows) : [];
  recordArrows(ctx, createdArrows, arrows);

  if (created.length > 0) {
    if (!ctx.ghostKey) await bringIntoView(created.map(i => i.id));
  }

  // Settle connection animations after a delay
  setTimeout(() => {
    for (const a of createdArrows) useArrowStore.getState().updateArrow(a.id, { animated: false });
  }, 1500);
}

async function handleCreateFolder(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const store = useCanvasStore.getState();
  if (!store.currentBoardId) return;

  const name = (payload.name as string) || 'New Folder';
  const position = findOpenSpot(200, 150);
  const board = await store.createBoard(name, store.currentBoardId, position, {
    id: payload.folder_id as string | undefined,
    iconColor: payload.color as string | undefined,
  });
  if (!board) return;

  const itemIds = (payload.item_ids as string[] | undefined) ?? [];
  // Undo can only put back what it knows came from this board
  const fromHere = useCanvasStore.getState().items.filter(i => itemIds.includes(i.id) && !i.isGhost);
  const previousPositions = new Map(fromHere.map(i => [i.id, { x: i.position_x, y: i.position_y }]));
  if (itemIds.length > 0) {
    await useCanvasStore.getState().moveItemsToBoard(itemIds, board.id);
  }
  record(ctx, {
    type: 'CREATE_FOLDER',
    timestamp: Date.now(),
    boardId: board.id,
    itemIds: fromHere.map(i => i.id),
    previousBoardId: store.currentBoardId,
    previousPositions,
    // What redo needs to make the folder again
    newContent: { name, parentId: store.currentBoardId, x: position.x, y: position.y, iconColor: payload.color as string | undefined },
  });
}

/** A link card's content from metadata the model copied out of web_search; empty fields fall back to the URL. */
export function linkContentFromPreview(url: string, preview: Record<string, unknown> | undefined): LinkItemContent {
  if (!preview) return fallbackLinkContent(url);
  return mergeLinkMetadata(url, {
    title: preview.title,
    description: preview.description,
    image: preview.image,
    siteName: preview.site,
    favicon: preview.favicon,
  });
}

/**
 * Link cards appear at once from the metadata web_search already had (or
 * from the URL alone); real previews are fetched afterwards and patched in.
 */
async function handleAddLinks(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const requested = (payload.urls as string[] | undefined) ?? [];
  const previews = (payload.previews as Record<string, Record<string, unknown>> | undefined) ?? {};
  const store = useCanvasStore.getState();
  const boardId = (payload.board_id as string | null) || store.currentBoardId;
  if (requested.length === 0 || !boardId) return;

  // A link already on the open board (or twice in the call) is not added again:
  // the model can name one the user or an earlier reply already put there
  const onCurrent = boardId === store.currentBoardId;
  const { fresh: urls, kept, skipped: skippedHere } = onCurrent
    ? newSourcesOnly(requested, url => url, sourceKeysOf(store.items))
    : { fresh: requested, kept: requested.map((_, i) => i), skipped: 0 };
  // Add all into a new folder says how many it left out before it got here
  const skipped = skippedHere + (typeof payload.skipped === 'number' ? payload.skipped : 0);
  if (urls.length === 0) {
    if (ctx.ghostKey) usePartnrGhostStore.getState().fade(ctx.ghostKey);
    toast(alreadyHereMessage(0, skipped));
    return;
  }
  // The streamed ghosts were one per requested link: drop the skipped ones so the rest land over their cards
  if (skippedHere > 0 && ctx.ghostKey) usePartnrGhostStore.getState().keepSlots(ctx.ghostKey, kept);

  const contents = urls.map(url => linkContentFromPreview(url, previews[url]));
  const newItems = contents.map(content => ({
    item_type: 'link' as ItemType,
    content: content as unknown as Record<string, unknown>,
    width: LINK_CARD_WIDTH,
    height: linkCardHeight(content),
  }));

  const created = onCurrent
    ? await landOnCurrentBoard({
        ctx,
        toolName: 'add_links',
        items: newItems,
        ghostSpecs: contents.map(c => ({ kind: 'link', itemType: 'link', title: c.title || c.siteName, body: c.description, width: LINK_CARD_WIDTH, height: linkCardHeight(c), complete: true })),
      })
    : await placeItemsOnBoard(ctx, boardId, newItems);

  if (created.length === 0) {
    toast.error('Failed to add links. Please try again.');
    return;
  }
  if (onCurrent) {
    if (skipped > 0) toast.success(alreadyHereMessage(created.length, skipped));
    if (!ctx.ghostKey) await bringIntoView(created.map(i => i.id));
  } else {
    const board = useCanvasStore.getState().boards.find(b => b.id === boardId);
    const already = skipped > 0 ? `, ${skipped} ${skipped === 1 ? 'was' : 'were'} already here` : '';
    toast.success(`Added ${created.length} link${created.length === 1 ? '' : 's'}${board ? ` to ${board.name}` : ''}${already}`);
  }
  void refreshLinkPreviews(created);
}

/** Fetches each card's real preview in the background and patches it in when it differs. */
async function refreshLinkPreviews(created: Item[]): Promise<void> {
  await Promise.all(created.map(async (item) => {
    const content = item.content as unknown as LinkItemContent;
    const fresh = await fetchFreshLinkPreview(content.url);
    if (!fresh) return;
    // Keep what the search already had where the page gave nothing better
    // (a blocked page comes back titled with its domain, or its path from mergeLinkMetadata)
    const weakTitle = !fresh.title || fresh.title === content.siteName || fresh.title === titleFromPath(content.url) || hostOf(fresh.title) === hostOf(content.url);
    const merged: LinkItemContent = {
      ...fresh,
      title: weakTitle && content.title ? content.title : fresh.title || content.title,
      description: fresh.description || content.description,
      image: fresh.image || content.image,
      favicon: fresh.favicon || content.favicon,
      siteName: fresh.siteName || content.siteName,
    };
    const height = linkCardHeight(merged, item.width);
    const state = useCanvasStore.getState();
    const loaded = state.items.some(i => i.id === item.id) || state.allItems.some(i => i.id === item.id);
    if (!loaded) return; // undone or deleted meanwhile
    await state.updateItem(item.id, { content: merged as unknown as Json, ...(height !== item.height ? { height } : {}) });
  }));
}

interface ImagePayload {
  url: string;
  width?: number | null;
  height?: number | null;
  mime_type?: string;
  caption?: string | null;
  alt?: string | null;
  source_url?: string | null;
  original_url?: string | null;
}

async function handleAddImages(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const images = ((payload.images as ImagePayload[] | undefined) ?? []).filter(img => typeof img?.url === 'string');
  if (images.length === 0) return;
  const store = useCanvasStore.getState();
  const boardId = (payload.board_id as string | undefined) || store.currentBoardId;
  if (!boardId) return;

  const newItems = images.map(img => {
    const size = imageCardSize(img.width, img.height);
    return {
      item_type: 'image' as ItemType,
      content: {
        url: img.url,
        alt: img.alt || img.caption || 'Image',
        ...(img.mime_type ? { mimeType: img.mime_type } : {}),
        ...(img.caption ? { caption: img.caption } : {}),
        ...(img.source_url ? { source_url: img.source_url } : {}),
        ...(img.original_url ? { original_url: img.original_url } : {}),
        partnr_generated: true,
      },
      width: size.width,
      height: size.height,
    };
  });

  if (boardId !== store.currentBoardId) {
    const created = await placeItemsOnBoard(ctx, boardId, newItems);
    const board = useCanvasStore.getState().boards.find(b => b.id === boardId);
    if (created.length) toast.success(`Added ${created.length} image${created.length === 1 ? '' : 's'}${board ? ` to ${board.name}` : ''}`);
    return;
  }
  const created = await landOnCurrentBoard({
    ctx,
    toolName: 'add_images',
    items: newItems,
    placement: payload.placement as CreatePlacement | undefined,
    ghostSpecs: images.map((img, i) => ({ kind: 'image', itemType: 'image', title: img.caption ?? '', body: '', width: newItems[i].width, height: newItems[i].height, complete: true })),
  });
  if (created.length > 0 && !ctx.ghostKey) await bringIntoView(created.map(i => i.id));
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function safeHref(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

/** A quote card: the quote as a blockquote, the note under it, and the source linked. */
export function clipContent(payload: Record<string, unknown>): { title: string; body: string } {
  const quote = String(payload.quote ?? '').trim();
  const note = String(payload.note ?? '').trim();
  const href = safeHref(payload.source_url);
  const title = String(payload.source_title ?? '').trim();
  const site = String(payload.site ?? '').trim();
  const source = href
    ? `<p>Source: <a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(title || site || href)}</a>${site && title ? ` (${escapeHtml(site)})` : ''}</p>`
    : '';
  return {
    title: '',
    body: `<blockquote><p>${escapeHtml(quote)}</p></blockquote>${note ? `<p>${escapeHtml(note)}</p>` : ''}${source}`,
  };
}

async function handleClipToBoard(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const quote = String(payload.quote ?? '').trim();
  if (!quote) return;
  const store = useCanvasStore.getState();
  const boardId = (payload.board_id as string | undefined) || store.currentBoardId;
  if (!boardId) return;
  const content = { ...clipContent(payload), partnr_generated: true, source_url: safeHref(payload.source_url) };
  const item = {
    item_type: 'text_card' as ItemType,
    content,
    width: CLIP_CARD_WIDTH,
    height: clipCardHeight(quote, String(payload.note ?? '')),
  };
  if (boardId !== store.currentBoardId) {
    await placeItemsOnBoard(ctx, boardId, [item]);
    return;
  }
  await landOnCurrentBoard({
    ctx,
    toolName: 'clip_to_board',
    items: [item],
    placement: payload.placement as CreatePlacement | undefined,
    ghostSpecs: [{ kind: 'quote', itemType: 'text_card', title: String(payload.source_title ?? ''), body: quote, width: item.width, height: item.height, complete: true }],
  });
}

/**
 * A text card or document body from Partnr (markdown) as the editor's HTML,
 * so lists, bold and headings arrive formatted. Lines a model starts with a
 * typed bullet (•, ·, ◦, ▪) become list items rather than literal dots.
 */
export function partnrBodyHtml(body: string): string {
  if (!body.trim()) return '';
  return partnrMarkdownToHtml(body.replace(/^([ \t]*)[•·◦▪]\s+/gm, '$1- '));
}

/**
 * Markdown from Partnr to the editor's HTML. The text may carry words from
 * web pages, so HTML in it is escaped first; then markdownToHtml, plus
 * [text](url) links, which it does not handle.
 */
export function partnrMarkdownToHtml(markdown: string): string {
  // A newline after each block keeps words apart where cards read the HTML as plain text
  const html = markdownToHtml(escapeHtml(markdown).replace(/&#39;/g, "'")).replace(/(<\/(?:h[1-6]|p|ul|ol|li|blockquote)>)(?!\n)/g, '$1\n');
  return html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, text: string, url: string) => {
    // The URL was escaped with the rest; undo &amp; for the check, escape again for the attribute
    const href = safeHref(url.replace(/&amp;/g, '&'));
    return href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${text}</a>` : text;
  });
}

async function handleCreateDocument(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const markdown = String(payload.markdown ?? '');
  if (!markdown.trim()) return;
  const store = useCanvasStore.getState();
  const boardId = (payload.board_id as string | undefined) || store.currentBoardId;
  if (!boardId) return;
  const body = partnrMarkdownToHtml(markdown);
  const wordCount = markdown.split(/\s+/).filter(Boolean).length;
  const item = {
    item_type: 'document' as ItemType,
    content: { title: String(payload.title ?? 'Untitled'), body, wordCount, partnr_generated: true },
    width: getDefaultWidth('document'),
    height: getDefaultHeight('document'),
  };
  if (boardId !== store.currentBoardId) {
    await placeItemsOnBoard(ctx, boardId, [item]);
    return;
  }
  const created = await landOnCurrentBoard({
    ctx,
    toolName: 'create_document',
    items: [item],
    placement: payload.placement as CreatePlacement | undefined,
    ghostSpecs: [{ kind: 'card', itemType: 'document', title: String(payload.title ?? ''), body: '', width: item.width, height: item.height, complete: true }],
  });
  if (created[0]) {
    if (!ctx.ghostKey) await bringIntoView([created[0].id]);
    if (payload.open === true) usePartnrOpenDocumentStore.getState().request(created[0].id);
  }
}

interface ItemUpdate {
  item_id: string;
  item_type: string;
  linked_board_id?: string;
  title?: string;
  body?: string;
  color?: string;
}

async function handleUpdateItems(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const updates = (payload.updates as ItemUpdate[] | undefined) ?? [];

  for (const u of updates) {
    if (u.item_type === 'board_link') {
      if (!u.linked_board_id) continue;
      const boardUpdates: Partial<Board> = {};
      if (u.title) boardUpdates.name = u.title;
      if (u.color) boardUpdates.icon_color = u.color;
      if (Object.keys(boardUpdates).length === 0) continue;
      await useCanvasStore.getState().updateBoard(u.linked_board_id, boardUpdates);
      // updateBoard only refreshes folder cards on the current board
      if (!useCanvasStore.getState().items.some(i => i.id === u.item_id)) {
        await patchOffBoardItemContent(u.item_id, {
          ...(boardUpdates.name ? { name: boardUpdates.name } : {}),
          ...(boardUpdates.icon_color ? { color: boardUpdates.icon_color } : {}),
        });
      }
      continue;
    }

    const patch = buildContentPatch(u);
    if (!patch) continue;

    const store = useCanvasStore.getState();
    const existing = store.items.find(i => i.id === u.item_id) ?? store.allItems.find(i => i.id === u.item_id);
    if (existing) {
      const previousContent = (existing.content ?? {}) as Record<string, unknown>;
      // updateItem merges content, so title-only edits keep the body
      await store.updateItem(u.item_id, { content: patch as Json });
      record(ctx, {
        type: 'UPDATE_CONTENT',
        timestamp: Date.now(),
        itemId: u.item_id,
        previousContent,
        newContent: { ...previousContent, ...patch },
      });
    } else {
      await patchOffBoardItemContent(u.item_id, patch);
    }
  }
}

function buildContentPatch(u: ItemUpdate): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};
  switch (u.item_type) {
    case 'sticky':
      if (u.body !== undefined || u.title !== undefined) patch.text = u.body ?? u.title;
      if (u.color) Object.assign(patch, stickyColorFields(u.color));
      break;
    case 'document':
      if (u.title !== undefined) patch.title = u.title;
      if (u.body !== undefined) patch.body = partnrBodyHtml(u.body);
      break;
    case 'text_card':
      if (u.title !== undefined) patch.title = u.title;
      if (u.body !== undefined) patch.body = partnrBodyHtml(u.body);
      break;
    case 'link':
      if (u.title !== undefined) patch.title = u.title;
      if (u.body !== undefined) patch.description = u.body;
      break;
    default:
      if (u.title !== undefined) patch.title = u.title;
      if (u.body !== undefined) patch.body = u.body;
      if (u.color) patch.color = u.color;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** Merge a content patch into an item that isn't loaded in the store (e.g. inside another folder). */
async function patchOffBoardItemContent(id: string, patch: Record<string, unknown>): Promise<void> {
  if (Object.keys(patch).length === 0) return;
  const { data, error } = await supabase.from('items').select('content').eq('id', id).maybeSingle();
  if (error || !data) {
    console.error('[CanvasAction] Could not load item to update:', id, error);
    return;
  }
  const content = { ...((data.content as Record<string, unknown>) || {}), ...patch };
  const { error: updateError } = await supabase.from('items').update({ content: content as Json }).eq('id', id);
  if (updateError) {
    console.error('[CanvasAction] Update failed:', id, updateError);
    return;
  }
  useCanvasStore.setState(state => ({
    allItems: state.allItems.map(i => (i.id === id ? { ...i, content: content as Json } : i)),
  }));
}

async function handleMoveItems(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const itemIds = (payload.item_ids as string[] | undefined) ?? [];
  if (itemIds.length === 0) return;
  const store = useCanvasStore.getState();

  const targetBoardId = payload.target_board_id as string | undefined;
  if (targetBoardId) {
    const loaded = store.items.filter(i => itemIds.includes(i.id));
    await store.moveItemsToBoard(itemIds, targetBoardId);
    if (store.currentBoardId && loaded.length) {
      record(ctx, {
        type: 'MOVE_TO_BOARD',
        timestamp: Date.now(),
        itemIds: loaded.map(i => i.id),
        previousBoardId: store.currentBoardId,
        newBoardId: targetBoardId,
        previousPositions: new Map(loaded.map(i => [i.id, { x: i.position_x, y: i.position_y }])),
      });
    }
    return;
  }

  const items = itemIds
    .map(id => store.items.find(i => i.id === id))
    .filter((i): i is NonNullable<typeof i> => !!i && !i.isGhost);
  if (items.length === 0) return;

  const positions = new Map<string, { x: number; y: number }>();
  const offset = payload.offset as { dx?: number; dy?: number } | undefined;
  const nearItem = store.items.find(i => i.id === payload.near_item_id);

  if (nearItem) {
    const GAP = 60;
    const sized = items.map(i => ({ id: i.id, width: i.width, height: i.height }));
    const maxWidth = Math.max(...sized.map(i => i.width));
    const maxHeight = Math.max(...sized.map(i => i.height));
    const relation = (payload.relation as string) || 'right';
    const layout =
      relation === 'left' || relation === 'right'
        ? computeVerticalLayout(sized, {
            startX: relation === 'right' ? nearItem.position_x + nearItem.width + GAP : nearItem.position_x - GAP - maxWidth,
            startY: nearItem.position_y,
            alignX: 'left',
          })
        : computeHorizontalLayout(sized, {
            startX: nearItem.position_x,
            startY: relation === 'bottom' ? nearItem.position_y + nearItem.height + GAP : nearItem.position_y - GAP - maxHeight,
          });
    layout.forEach((pos, id) => positions.set(id, pos));
  } else if (offset) {
    for (const item of items) {
      positions.set(item.id, { x: item.position_x + (offset.dx ?? 0), y: item.position_y + (offset.dy ?? 0) });
    }
  }

  if (positions.size === 0) return;
  record(ctx, {
    type: 'MOVE_ITEMS',
    timestamp: Date.now(),
    previousPositions: new Map(items.filter(i => positions.has(i.id)).map(i => [i.id, { x: i.position_x, y: i.position_y }])),
    newPositions: new Map(positions),
  });
  await applyLayout(positions, { animate: true });
  await handleFocusViewport({ itemIds: Array.from(positions.keys()) });
}

async function handleDeleteItems(payload: Record<string, unknown>, ctx: ExecContext): Promise<void> {
  const items = (payload.items as Array<{ id: string; item_type: string; label?: string }> | undefined) ?? [];
  if (items.length === 0) return;

  const count = `${items.length} item${items.length === 1 ? '' : 's'}`;
  // Partnr 2 asked inline before the tool ran (toolApproval); the old stream still asks here
  if (payload.approved !== true) {
    const hasFolder = items.some(i => i.item_type === 'board_link');
    const confirmed = await usePartnrConfirmStore.getState().ask({
      title: `Delete ${count}?`,
      description: `Partnr wants to delete ${items.length === 1 ? 'this item' : 'these items'}.${hasFolder ? ' Deleting a folder also deletes everything inside it.' : ''} This can't be undone.`,
      items: items.map(i => i.label || i.item_type),
      confirmLabel: 'Delete',
    });

    if (!confirmed) {
      toast.info('Nothing was deleted');
      return;
    }
  }

  // Loaded cards can come back with Undo; folders cannot (a deleted board takes its contents)
  const state = useCanvasStore.getState();
  const restorable = items
    .filter(i => i.item_type !== 'board_link')
    .map(i => state.items.find(x => x.id === i.id) ?? state.allItems.find(x => x.id === i.id))
    .filter((i): i is Item => !!i && !i.isGhost);
  await state.deleteItems(items.map(i => i.id));
  if (restorable.length) {
    record(ctx, { type: 'DELETE_ITEMS', timestamp: Date.now(), items: restorable });
  }
  toast.success(`Deleted ${count}`);
}

async function handleFocusViewport(payload: Record<string, unknown>): Promise<void> {
  const store = useCanvasStore.getState();
  const itemIds = payload.itemIds as string[];

  const items = itemIds
    .map(id => store.items.find(i => i.id === id))
    .filter(Boolean);

  if (items.length === 0) return;

  // Compute bounding box
  const padding = 80;
  const bounds = {
    minX: Math.min(...items.map(i => i!.position_x)) - padding,
    maxX: Math.max(...items.map(i => i!.position_x + i!.width)) + padding,
    minY: Math.min(...items.map(i => i!.position_y)) - padding,
    maxY: Math.max(...items.map(i => i!.position_y + i!.height)) + padding,
  };

  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;
  // The part of the window the canvas shows: left of the open Partnr panel
  const windowWidth = window.innerWidth - usePartnrPanelStore.getState().rightInset;
  const windowHeight = window.innerHeight;

  const zoomToFit = (payload.zoomLevel as number) ?? Math.min(
    windowWidth / boundsWidth,
    windowHeight / boundsHeight,
    1.0
  );

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  // The canvas draws a point at (x + pan) * zoom, so the centre lands mid-view at pan = half view / zoom - centre
  useCanvasStore.setState({
    panX: windowWidth / 2 / zoomToFit - centerX,
    panY: windowHeight / 2 / zoomToFit - centerY,
    zoom: zoomToFit,
  });
}

/**
 * Brings items into view only when some are outside it. Partnr 2 calls it
 * once per reply, after every action landed (usePartnrChat), so the camera
 * stays still while Partnr works; the old stream calls it per action.
 */
export async function bringIntoView(itemIds: string[]): Promise<void> {
  if (itemIds.length === 0) return;
  const view = visibleCanvasRect();
  const items = useCanvasStore.getState().items.filter(i => itemIds.includes(i.id));
  const outside = items.some(i =>
    i.position_x < view.x || i.position_y < view.y || i.position_x + i.width > view.x + view.width || i.position_y + i.height > view.y + view.height,
  );
  if (outside) await handleFocusViewport({ itemIds });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export interface CreatePlacement {
  strategy?: string;
  relation?: string;
  resolvedItemId?: string;
}

const PLACEMENT_RELATIONS = new Set(['top', 'bottom', 'left', 'right']);

/**
 * Move a laid-out block of new items into free space: beside the item Partnr
 * named when it is on this board, else the nearest open spot to the viewport
 * centre. Without this, Partnr's notes landed on top of whatever was there.
 */
export function moveBlockToOpenSpot(
  sized: Array<{ id: string; width: number; height: number }>,
  positions: Map<string, { x: number; y: number }>,
  placement?: CreatePlacement,
): Map<string, { x: number; y: number }> {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of sized) {
    const pos = positions.get(s.id);
    if (!pos) continue;
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + s.width);
    maxY = Math.max(maxY, pos.y + s.height);
  }
  if (!Number.isFinite(minX)) return positions;
  const width = maxX - minX;
  const height = maxY - minY;

  const store = useCanvasStore.getState();
  const existingItems = [
    ...store.items
      .filter(i => !i.isGhost)
      .map(i => ({ id: i.id, x: i.position_x, y: i.position_y, width: i.width, height: i.height })),
    // Ghost blocks of other tool calls still landing count as taken space
    ...ghostObstacles(usePartnrGhostStore.getState().blocks),
  ];
  const relation = placement?.relation ?? 'right';
  const anchorOnBoard = placement?.resolvedItemId && existingItems.some(i => i.id === placement.resolvedItemId);

  const origin = anchorOnBoard && PLACEMENT_RELATIONS.has(relation)
    ? calculatePlacement(
        { type: 'relative_to_item', targetItemId: placement!.resolvedItemId!, relation: relation as 'top' | 'bottom' | 'left' | 'right' },
        { existingItems, viewportBounds: { left: 0, top: 0, right: 0, bottom: 0 }, itemToPlace: { width, height } },
      )
    : findOpenSpot(width, height, existingItems);

  const dx = origin.x - minX;
  const dy = origin.y - minY;
  const moved = new Map<string, { x: number; y: number }>();
  for (const [id, pos] of positions) moved.set(id, { x: pos.x + dx, y: pos.y + dy });
  return moved;
}

/** The middle of the canvas the user can see: left of the Partnr panel when it is open. */
export function visibleViewportCenter(): { x: number; y: number } {
  const view = visibleCanvasRect();
  return { x: view.x + view.width / 2, y: view.y + view.height / 2 };
}

/** The part of the canvas the user can see (left of the open Partnr panel), in canvas coordinates. */
export function visibleCanvasRect(): { x: number; y: number; width: number; height: number } {
  const store = useCanvasStore.getState();
  const inset = usePartnrPanelStore.getState().rightInset;
  const zoom = store.zoom || 1;
  const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const h = typeof window !== 'undefined' ? window.innerHeight : 1080;
  return { x: -store.panX, y: -store.panY, width: Math.max(1, (w - inset) / zoom), height: h / zoom };
}

/** A free spot near the visible centre for an item of this size. */
export function findOpenSpot(
  width: number,
  height: number,
  obstacles?: Array<{ id: string; x: number; y: number; width: number; height: number }>,
): { x: number; y: number } {
  const store = useCanvasStore.getState();
  const center = visibleViewportCenter();
  return ensureNoCollision(
    { x: center.x - width / 2, y: center.y - height / 2 },
    {
      existingItems: obstacles ?? [
        ...store.items
          .filter(i => !i.isGhost)
          .map(i => ({ id: i.id, x: i.position_x, y: i.position_y, width: i.width, height: i.height })),
        ...ghostObstacles(usePartnrGhostStore.getState().blocks),
      ],
      viewportBounds: { left: 0, top: 0, right: 0, bottom: 0 },
      itemToPlace: { width, height },
    },
  );
}

interface NewItem {
  item_type: ItemType;
  content: Record<string, unknown>;
  width: number;
  height: number;
}

let executorGhosts = 0;

/**
 * Creates items on the current board in one insert, where their ghosts are.
 *
 * - With ghosts reserved for this tool call (Partnr 2 streamed its input),
 *   each item lands on its ghost's position; a placement the server resolved
 *   next to another item moves the block there first (the ghosts glide over).
 * - Without (the old stream, a reload), the items are laid out as before and
 *   ghosts are drawn for them right away, so both paths look the same.
 *
 * Then the ghosts leave in order over the real cards: the staggered reveal is
 * an animation over items that already exist.
 */
async function landOnCurrentBoard(options: {
  ctx: ExecContext;
  toolName: string;
  items: NewItem[];
  ghostSpecs: GhostSpec[];
  placement?: CreatePlacement;
  /** Layout around (0, 0) by index ('0', '1', ...); default: a grid */
  layoutPositions?: (sized: Array<{ id: string; width: number; height: number }>) => Map<string, { x: number; y: number }>;
}): Promise<Item[]> {
  const { items } = options;
  if (items.length === 0) return [];
  const store = useCanvasStore.getState();
  const ghosts = usePartnrGhostStore.getState();
  const sized = items.map((item, i) => ({ id: String(i), width: item.width, height: item.height }));

  const ghostKey = options.ctx.ghostKey;
  let block: GhostBlock | null = ghostKey ? ghosts.take(ghostKey) : null;
  if (block && block.boardId !== store.currentBoardId) {
    // The user changed boards while it streamed: those ghosts were elsewhere
    ghosts.fade(block.key);
    block = null;
  }

  let positions: Map<string, { x: number; y: number }>;
  let key: string;
  if (block) {
    key = block.key;
    // A finished diagram's ghosts already sit on its final layout; everything else flows
    // from the block's origin with the real sizes, which are the ghosts' sizes for cards
    // and the true shapes for images
    const flow = flowPositions(sized, block.columns, block.origin);
    positions = new Map(sized.map((s, i) => [s.id, block!.toolName === 'create_diagram' && block!.slots[i] ? { x: block!.slots[i].x, y: block!.slots[i].y } : flow[i]]));
    const anchor = options.placement?.resolvedItemId;
    const cards = store.items.filter(i => !i.isGhost);
    // Real sizes can outgrow the reserved block (a tall image): then the whole block moves to open space
    const covers = sized.some(s => {
      const p = positions.get(s.id)!;
      return cards.some(c => p.x < c.position_x + c.width && c.position_x < p.x + s.width && p.y < c.position_y + c.height && c.position_y < p.y + s.height);
    });
    if ((anchor && cards.some(i => i.id === anchor)) || covers) {
      positions = moveBlockToOpenSpot(sized, positions, options.placement);
    }
    // Ghosts glide to anything that moved before they fade over the real cards
    ghosts.moveSlots(key, sized.map(s => positions.get(s.id)));
  } else {
    const laidOut = options.layoutPositions?.(sized) ?? computeGridLayout(sized, { columns: Math.ceil(Math.sqrt(sized.length)), spacing: 40 });
    positions = moveBlockToOpenSpot(sized, laidOut, options.placement);
    key = ghostKey ?? `exec-${Date.now().toString(36)}-${++executorGhosts}`;
    ghosts.remove(key);
    const first = positions.get('0') ?? { x: 0, y: 0 };
    ghosts.reserve({
      key,
      toolName: options.toolName,
      boardId: store.currentBoardId,
      origin: first,
      columns: Math.max(1, items.length),
      cell: { width: 1, height: 1 },
      slots: options.ghostSpecs.map((spec, i) => ({ ...spec, ...(positions.get(String(i)) ?? first) })),
    });
    ghosts.take(key);
  }

  let created: Item[] = [];
  try {
    created = await store.createItems(items.map((item, i): ItemInput => ({
      board_id: store.currentBoardId,
      item_type: item.item_type,
      content: item.content,
      position_x: positions.get(String(i))!.x,
      position_y: positions.get(String(i))!.y,
      width: item.width,
      height: item.height,
    })), { history: options.ctx.group });
  } catch (e) {
    // Ghosts must not outlive a failed landing
    console.error('[CanvasAction] Could not create items:', e);
  }
  if (created.length) usePartnrGhostStore.getState().land(key);
  else usePartnrGhostStore.getState().fade(key);
  return created;
}

/**
 * Create items as a grid on any of the user's boards, in one insert. On the
 * current board they land in open space near the visible centre; on another
 * board they go below whatever is already there. Returns the new items.
 */
async function placeItemsOnBoard(ctx: ExecContext, boardId: string, items: NewItem[]): Promise<Item[]> {
  if (items.length === 0) return [];
  const store = useCanvasStore.getState();
  const sized = items.map((item, i) => ({ id: String(i), width: item.width, height: item.height }));
  const columns = Math.ceil(Math.sqrt(items.length));
  const GAP = 40;
  const blockWidth = columns * (Math.max(...sized.map(i => i.width)) + GAP);
  const blockHeight = Math.ceil(items.length / columns) * (Math.max(...sized.map(i => i.height)) + GAP);

  let positions: Map<string, { x: number; y: number }>;
  if (boardId === store.currentBoardId) {
    const origin = findOpenSpot(blockWidth, blockHeight);
    positions = computeGridLayout(sized, { columns, spacing: GAP, startX: origin.x, startY: origin.y });
  } else {
    const { data: existing } = await supabase
      .from('items')
      .select('position_x, position_y, height')
      .eq('board_id', boardId);
    const rows = existing ?? [];
    const startX = rows.length ? Math.min(...rows.map(r => r.position_x)) : 100;
    const startY = rows.length ? Math.max(...rows.map(r => r.position_y + r.height)) + 80 : 100;
    positions = computeGridLayout(sized, { columns, spacing: GAP, startX, startY });
  }

  // One insert; createItems keeps them out of the open canvas when the board is another one
  const created = await useCanvasStore.getState().createItems(items.map((item, i) => ({
    board_id: boardId,
    item_type: item.item_type,
    content: item.content,
    position_x: positions.get(String(i))!.x,
    position_y: positions.get(String(i))!.y,
    width: item.width,
    height: item.height,
  })), { history: ctx.group });
  if (created.length === 0) console.error('[CanvasAction] Failed to create items in board:', boardId);
  return created;
}

function computeLayoutPositions(
  items: Array<{ id: string; width: number; height: number }>,
  layoutType: string,
  centerX: number,
  centerY: number
): Map<string, { x: number; y: number }> {
  switch (layoutType) {
    case 'grid':
      return computeGridLayout(items, { startX: centerX - 300, startY: centerY - 200 });
    case 'horizontal_row':
      return computeHorizontalLayout(items, { startX: centerX - 400, startY: centerY });
    case 'vertical_column':
      return computeVerticalLayout(items, { startX: centerX, startY: centerY - 300 });
    case 'radial':
      if (items.length > 1) {
        return computeRadialLayout(items[0], items.slice(1), { centerX, centerY });
      }
      return computeGridLayout(items, { startX: centerX, startY: centerY });
    case 'cluster':
      return computeClusterLayout(items, { centerX, centerY });
    default:
      return computeGridLayout(items, { startX: centerX, startY: centerY });
  }
}

function buildContentJsonb(item: {
  item_type?: string;
  title: string;
  body?: string;
  color?: string;
}): Record<string, unknown> {
  const type = item.item_type || 'sticky';

  if (type === 'sticky') {
    // Stickies have no title field, so keep Partnr's heading as the first line
    const body = item.body?.trim() ?? '';
    const title = item.title?.trim() ?? '';
    const text = title && body && !body.startsWith(title) ? `${title}\n\n${body}` : body || title;
    return { text, color: toStickyColorName(item.color) };
  }

  if (type === 'text_card') {
    // A text card shows only its body: Partnr's title becomes its heading,
    // unless the body already opens with it
    const title = item.title?.trim() ?? '';
    const body = item.body ?? '';
    const opensWithTitle = !!title && body.replace(/^[#*\s]+/, '').toLowerCase().startsWith(title.toLowerCase());
    return {
      title: item.title,
      body: partnrBodyHtml(title && !opensWithTitle ? `### ${title}\n\n${body}` : body),
    };
  }

  if (type === 'document') {
    return {
      title: item.title,
      body: partnrBodyHtml(item.body || ''),
    };
  }

  return { text: item.body || item.title };
}

function getConnectionTypeColor(type?: string): string {
  const colorMap: Record<string, string> = {
    default: '#94a3b8',
    causes: '#f97316',
    blocks: '#ef4444',
    contains: '#8b5cf6',
    sequence: '#3b82f6',
    related: '#6b7280',
  };
  return colorMap[type || 'default'] || '#94a3b8';
}

function getRoleColor(role?: string): string {
  const roleColors: Record<string, string> = {
    start: '#dcfce7',
    end: '#fee2e2',
    decision: '#fef3c7',
    process: '#dbeafe',
    input: '#ede9fe',
    output: '#cffafe',
    default: '#FEF3C7',
  };
  return roleColors[role || 'default'] || '#FEF3C7';
}

function getDefaultEdgeStyle(diagramType: string): 'bezier' | 'straight' | 'step' {
  switch (diagramType) {
    case 'flowchart': return 'step';
    case 'process': return 'bezier';
    case 'hierarchy': return 'step';
    case 'mind_map': return 'bezier';
    case 'comparison': return 'straight';
    default: return 'bezier';
  }
}

/** Pans and zooms to these items (receipts' Show). */
export async function showItemsOnCanvas(itemIds: string[]): Promise<void> {
  await handleFocusViewport({ itemIds });
}

/**
 * One link card at a canvas point (a web result dragged out of the Partnr
 * panel). Drawn at once from what the search had, refreshed in the background.
 */
export async function addLinkAt(url: string, preview: Record<string, unknown> | undefined, x: number, y: number): Promise<Item | null> {
  const store = useCanvasStore.getState();
  if (!store.currentBoardId) return null;
  const content = linkContentFromPreview(url, preview);
  const [created] = await store.createItems([{
    board_id: store.currentBoardId,
    item_type: 'link',
    content: content as unknown as Record<string, unknown>,
    position_x: x,
    position_y: y,
    width: LINK_CARD_WIDTH,
    height: linkCardHeight(content),
  }]);
  if (created) void refreshLinkPreviews([created]);
  return created ?? null;
}
